import React, { useEffect, useMemo, useState } from 'react'
import { PageHeader, CommonFilters, UserAvatar, TableDownload, CommonSelectorProps, CommonSelector } from '@components'
import { Card, Select, Row, InputNumber, Col, message, Table, Badge, TableProps, Radio, Button } from 'antd'
import { TableIdEnum, getTableXwidth, getSortOrder } from '@configs/table'
import { needTotalEnum, AIQualityCheckResultItem, AIQualityCheckResultFilter, AIQualityCheckPlanListFilter, AIQualityCheckPlanListItem } from '@types'
import { useTableService } from '@hooks/useTableService'
import { getAIQualityCheckResultList, ExportAIQualityCheckResultList } from '@services/task'
import { getAIQualityCheckPlanList } from '@services/ai-quality-check'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { MinusOutlined } from '@ant-design/icons'
import { isEmpty } from 'lodash'
import { isEmptyWithCustom, formatTime, trimStr, notEmptyArray } from '@library'
import { useTableColumn, TableColumnProps } from '@hooks'
import { antdSortMap } from '@configs/map'
import { Link } from 'react-router-dom'
import { QcType } from '../quality-check-reexamine-detail/constant'
import { QcSchemeStatus } from '@pages/config/quality-check-scheme-detail/constants'

const qualityCheckPlan: CommonSelectorProps<AIQualityCheckPlanListFilter, AIQualityCheckPlanListItem> = {
  id: 'planName',
  name: '方案名称',
  service: getAIQualityCheckPlanList,
  filter: { needTotal: needTotalEnum.LIST, status: QcSchemeStatus.optimized },
  valueKey: 'id',
  labelKey: 'name',
  showSearch: true,
  searchKey: 'planName',
  placeholderOnSearching: '按方案名称模糊搜索...',
  mode: 'multiple',
}

const initFilter = {
  page: 1,
  pageSize: 10,
  needTotal: needTotalEnum.LIST,
  manualReview: 1,
}
const qcTypeOptions = [
  { label: '违规判断', value: QcType.compliance },
  { label: '能力评分', value: QcType.moc },
]
const statusOptions = [
  { label: '待复核', value: 0 },
  { label: '已复核', value: 1 },
]

const hitKeyWordsOptions = [
  { label: '是', value: true },
  { label: '否', value: false },
]
const options = [
  { label: '>', value: 'gt' },
  { label: '≥', value: 'ge' },
  { label: '=', value: 'eq' },
  { label: '<', value: 'lt' },
  { label: '≤', value: 'le' },
]
const QualityCheckReexamineList = () => {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
  } = useTableService(getAIQualityCheckResultList, initFilter, '获取人工复核列表失败')

  const filterItemList: FieldItem[] = [
    {
      label: 'recordId',
      fieldName: 'recordIds',
      type: 'multiTags',
      handleValue: val => ({
        recordIds: val?.map(item => item ? +item : item),
      }),
    },
    {
      label: '任务ID',
      fieldName: 'taskIds',
      type: 'multiTags',
      handleValue: val => ({
        taskIds: val?.map(item => item ? +item : item),
      }),
    },
    {
      label: '方案类型',
      fieldName: 'qcType',
      render: () => {
        return (<Select mode="multiple" placeholder="未筛选则默认全部" allowClear options={qcTypeOptions} />)
      },
    },
    {
      label: '质检方案',
      fieldName: 'planIds',
      render: () => {
        return (<CommonSelector {...qualityCheckPlan} />)
      },
    },
    {
      label: '是否命中关键词',
      fieldName: 'hitKeyWords',
      render: () => {
        return (<Select placeholder="请选择" allowClear options={hitKeyWordsOptions} />)
      },
    },
    { fieldName: 'customerName', label: '客户名称', type: 'customerNameSelect' },
    { fieldName: 'cid', label: 'cid', type: 'multiTags' },
    {
      fieldName: 'salesName',
      label: '当前跟进人',
      type: 'staffSelectNull',
      handleValue: (val: string[]) => ({
        salesName: notEmptyArray(val) ? val?.map(v => v.replace('identifier', '')) : undefined,
      }),
    },
    {
      fieldName: 'callBeginTime',
      label: '通话日期',
      type: 'rangePicker',
      handleValue: val => ({
        callBeginTimeFrom: formatTime(val?.[0], 'start'),
        callBeginTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    {
      label: '质检状态',
      fieldName: 'status',
      render: () => {
        return (<Select placeholder="未筛选则默认全部" allowClear options={statusOptions} />)
      },
    },
    {
      label: '所属组',
      fieldName: 'groupId',
      type: 'groupId',
    },
    {
      label: '所属公司',
      fieldName: 'companyId',
      type: 'company',
    },
    {
      label: '复核落差-违规数',
      fieldName: 'hitRulesDifference',
      render: () => ({ value, onChange }) => {
        const hitRulesDifference = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={hitRulesDifference?.hitRulesDifference?.operator || undefined} onChange={val => {
                onChange({ hitRulesDifference: { operator: val, value: hitRulesDifference?.hitRulesDifference?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={hitRulesDifference?.hitRulesDifference?.value} min={0} max={99999999999} onChange={val => {
                onChange({ hitRulesDifference: { operator: hitRulesDifference?.hitRulesDifference?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          hitRulesDifference: {
            operator: val?.hitRulesDifference?.operator ?? undefined,
            value: val?.hitRulesDifference?.value ?? undefined,
          },
        }
      },
    },
    {
      label: '复核落差-评分',
      fieldName: 'scoreDifference',
      render: () => ({ value, onChange }) => {
        const scoreDifference = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={scoreDifference?.scoreDifference?.operator || undefined} onChange={val => {
                onChange({ scoreDifference: { operator: val, value: scoreDifference?.scoreDifference?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={scoreDifference?.scoreDifference?.value} min={0} max={99999999999} onChange={val => {
                onChange({ scoreDifference: { operator: scoreDifference?.scoreDifference?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          scoreDifference: {
            operator: val?.scoreDifference?.operator ?? undefined,
            value: val?.scoreDifference?.value ?? undefined,
          },
        }
      },
    },
    {
      label: '命中规则数',
      fieldName: 'aiHitRules',
      render: () => ({ value, onChange }) => {
        const aiHitRules = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={aiHitRules?.aiHitRules?.operator || undefined} onChange={val => {
                onChange({ aiHitRules: { operator: val, value: aiHitRules?.aiHitRules?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={aiHitRules?.aiHitRules?.value} min={0} max={99999999999} onChange={val => {
                onChange({ aiHitRules: { operator: aiHitRules?.aiHitRules?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          aiHitRules: {
            operator: val?.aiHitRules?.operator ?? undefined,
            value: val?.aiHitRules?.value ?? undefined,
          },
        }
      },
    },
    {
      label: '模型评分',
      fieldName: 'aiOverallScore',
      render: () => ({ value, onChange }) => {
        const aiOverallScore = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={aiOverallScore?.aiOverallScore?.operator || undefined} onChange={val => {
                onChange({ aiOverallScore: { operator: val, value: aiOverallScore?.aiOverallScore?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={aiOverallScore?.aiOverallScore?.value} min={0} max={99999999999} onChange={val => {
                onChange({ aiOverallScore: { operator: aiOverallScore?.aiOverallScore?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          aiOverallScore: {
            operator: val?.aiOverallScore?.operator ?? undefined,
            value: val?.aiOverallScore?.value ?? undefined,
          },
        }
      },
    },
    {
      label: '复核违规数',
      fieldName: 'reviewHitRules',
      render: () => ({ value, onChange }) => {
        const reviewHitRules = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={reviewHitRules?.reviewHitRules?.operator || undefined} onChange={val => {
                onChange({ reviewHitRules: { operator: val, value: reviewHitRules?.reviewHitRules?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={reviewHitRules?.reviewHitRules?.value} min={0} max={99999999999} onChange={val => {
                onChange({ reviewHitRules: { operator: reviewHitRules?.reviewHitRules?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          reviewHitRules: {
            operator: val?.reviewHitRules?.operator ?? undefined,
            value: val?.reviewHitRules?.value ?? undefined,
          },
        }
      },
    },
    {
      label: '复核总分',
      fieldName: 'reviewOverallScore',
      render: () => ({ value, onChange }) => {
        const reviewOverallScore = value || {}
        return (
          <Row justify="space-between" align="middle">
            <Col span={10}>
              <Select placeholder="</=/>" allowClear options={options} value={reviewOverallScore?.reviewOverallScore?.operator || undefined} onChange={val => {
                onChange({ reviewOverallScore: { operator: val, value: reviewOverallScore?.reviewOverallScore?.value } })
              }} />
            </Col>
            <Col span={1}>
              <MinusOutlined />
            </Col>
            <Col span={10}>
              <InputNumber value={reviewOverallScore?.reviewOverallScore?.value} min={0} max={99999999999} onChange={val => {
                onChange({ reviewOverallScore: { operator: reviewOverallScore?.reviewOverallScore?.operator, value: val } })
              }} />
            </Col>
          </Row>
        )
      },
      handleValue: (val) => {
        return {
          reviewOverallScore: {
            operator: val?.reviewOverallScore?.operator ?? undefined,
            value: val?.reviewOverallScore?.value ?? undefined,
          },
        }
      },
    },
  ]

  const columns: TableColumnProps<AIQualityCheckResultItem>[] = useMemo(() => [
    {
      title: 'id',
      dataIndex: 'id',
      width: 120,
    },
    {
      title: 'recordId',
      dataIndex: 'recordId',
      width: 120,
      render: (recordId, record) => (
        <Link target="_blank" to={`/task/quality-check/reexamine-detail/${record.id}?recordId=${recordId}`}>
          {recordId}
        </Link>
      ),
    },
    {
      title: 'taskId',
      dataIndex: 'taskId',
      width: 120,
    },
    {
      title: 'cid',
      dataIndex: 'cid',
      width: 200,
    },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      width: 280,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>),
    },
    { title: '坐席名', dataIndex: 'salesName', width: 260, render: value => <UserAvatar salesName={value} /> },
    {
      title: '所属组',
      dataIndex: 'groupName',
      width: 120,
      render: (name) => name.join(','),
    },
    {
      title: '所属公司',
      dataIndex: 'companyName',
      width: 120,
    },
    {
      title: '通话时间',
      dataIndex: 'callBeginTime',
      width: 180,
      sorter: true,
      sortOrder: filter.sort === 'callBeginTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number) => {
        const text = status === 1 ? '已复核' : '待复核'
        const color = status === 1 ? 'green' : 'red'
        return <Badge key={text} color={color} text={text} />
      },
    },
    { title: '质检人', dataIndex: 'reviewer', width: 260, render: value => <UserAvatar salesName={value} /> },
    {
      title: '方案类型',
      dataIndex: 'qcTypes',
      width: 150,
      render: (qcTypes) => (
        qcTypes?.join(';')
      ),
    },
    {
      title: '质检方案',
      dataIndex: 'planNames',
      width: 200,
      render: (name) => name ? name?.join(',') : '-',
    },
    {
      title: '是否命中关键词',
      dataIndex: 'hitKeyWords',
      width: 180,
      render: (hit: boolean) => (
        <Badge
          color={hit ? 'red' : 'green'}
          text={hit ? '是' : '否'}
        />
      ),
    },
    {
      title: '命中规则数',
      dataIndex: 'aiHitRules',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'aiHitRules' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '复核违规数',
      dataIndex: 'reviewHitRules',
      width: 130,
      sorter: true,
      sortOrder: filter.sort === 'reviewHitRules' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '模型评分',
      dataIndex: 'aiScore',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'aiScore' ? antdSortMap[filter.sortType] : undefined,

    },
    {
      title: '复核总分',
      dataIndex: 'reviewScore',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'reviewScore' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (name, record) => (
        <Link target="_blank" to={`/task/quality-check/reexamine-detail/${record.id}?recordId=${record.recordId}`}>
          复核
        </Link>
      ),
    },

  ], [filter])
  const [, columnProps] = useTableColumn(TableIdEnum.QUALITY_INSPECTION_MANUAL_LIST, columns)

  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])
  const onTableChange: TableProps<AIQualityCheckResultItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<AIQualityCheckResultFilter>(sorter)
    setFilter(sort)
  }

  const validations = [
    { field: 'hitRulesDifference', message: '复核落差-违规数需要同时设置操作符和值' },
    { field: 'scoreDifference', message: '复核落差-评分需要同时设置操作符和值' },
    { field: 'aiHitRules', message: '命中规则数需要同时设置操作符和值' },
    { field: 'aiOverallScore', message: '模型评分需要同时设置操作符和值' },
    { field: 'reviewHitRules', message: '复核违规数需要同时设置操作符和值' },
    { field: 'reviewOverallScore', message: '复核总分需要同时设置操作符和值' },
  ]
  const validateFilterField = (filter: any, fieldName: string, errorMessage: string) => {
    const operator = filter?.[fieldName]?.operator
    const value = filter?.[fieldName]?.value

    if (isEmpty(filter?.[fieldName]) || (!operator && isEmptyWithCustom(value))) {
      filter[fieldName] = undefined
      return true
    }

    if ((operator && isEmptyWithCustom(value)) || (!operator && !isEmptyWithCustom(value))) {
      message.error(errorMessage)
      return false
    }
    return true
  }

  return (
    <div className="quality-inspection-manual-list-page">
      <PageHeader title="人工复核" />
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label"
          itemList={filterItemList}
          onSubmit={(filter) => {
            for (const { field, message } of validations) {
              if (!validateFilterField(filter, field, message)) {
                return
              }
            }
            setFilter({ ...filter, page: 1 })
          }}
          onReset={() => {
            resetFilter({ ...initFilter })
          }}
          tableId={TableIdEnum.QUALITY_INSPECTION_MANUAL_LIST}
          standstill={10}
        />
      </Card>
      <Card>
        <TableDownload btnText="下载能力评分方案数据" className="ml20" exportMethod={ExportAIQualityCheckResultList} filter={{ ...filter, exportType: 1, }} />
        <TableDownload btnText="下载违规判断方案数据" className="ml20" exportMethod={ExportAIQualityCheckResultList} filter={{ ...filter, exportType: 2, }} />
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={list}
          columns={columnProps}
          onChange={onTableChange}
        />
      </Card>
    </div>
  )
}

export default QualityCheckReexamineList
